import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import React from 'react'

import SearchBar from '@/app/component/atom/search-bar'
import type { FileType } from '@/app/types'

import AudioFileTableRow from './audio-file-table-row'

export type AudioTableFile = FileType & {
  removable?: boolean
}

export type AudioTableFiles = Record<string, AudioTableFile>

type Props = {
  files: AudioTableFiles | undefined | null
  selectedFilename?: string

  onRemove?: (filename: string) => void
  onSelect?: (filename: string) => void
}

const AudioFileTable: React.FC<Props> = ({ files, selectedFilename, onRemove, onSelect }) => {
  const searchId = React.useId()
  const entries = React.useMemo(() => Object.entries(files || {}), [files])
  const [searchTerm, setSearchTerm] = React.useState('')
  const selectedFile = entries.find(([, file]) => file.filename === selectedFilename)?.[1]
  const selectedName = selectedFile?.title || selectedFilename
  const filteredEntries = React.useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase()

    if (!normalizedSearchTerm) return entries

    return entries.filter(([, file]) =>
      [file.filename, file.title, file.transcription].some((value) =>
        value?.toLowerCase().includes(normalizedSearchTerm),
      ),
    )
  }, [entries, searchTerm])

  return (
    <>
      <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {onSelect && (
          <Typography
            color="text.secondary"
            noWrap
            role="status"
            sx={{ flex: '1 1 160px', minWidth: 0 }}
            title={selectedName}
            variant="body2"
          >
            {selectedFilename ? `Selected: ${selectedName}` : 'No file selected'}
          </Typography>
        )}
        <Box sx={{ flex: '0 1 320px', minWidth: 0, ml: 'auto' }}>
          <SearchBar data-testid="audio-file-search" id={searchId} searchTerm={searchTerm} onChange={setSearchTerm} />
        </Box>
      </Box>
      {filteredEntries.length ? (
        <TableContainer>
          <Table size="small" data-testid="audio-file-table" sx={{ minWidth: 700, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {onSelect && <TableCell width="8%">Select</TableCell>}
                <TableCell width="30%">File</TableCell>
                <TableCell width="30%">Player</TableCell>
                <TableCell width={`${40 - (onSelect ? 8 : 0) - (onRemove ? 15 : 0)}%`}>Transcript</TableCell>
                {onRemove && (
                  <TableCell align="center" width="15%">
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries.map(([id, file]) => (
                <AudioFileTableRow
                  key={id}
                  file={file}
                  selected={selectedFilename === file.filename}
                  selectionName={`${searchId}-selection`}
                  onRemove={onRemove}
                  onSelect={onSelect}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary" variant="body2">
          No audio files match your search
        </Typography>
      )}
    </>
  )
}

export default AudioFileTable
export type { Props as AudioFileTableProps }
